import { format, addDays, subDays, getDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { Menu, MenuShort } from "~/entities/menuEntities";
import { DaysOfWeek, TimeOfDay } from "~/entities/siteEntities";
import uniqBy from "lodash/uniqBy";

const createDate = (baseDate: Date, timeOfDay: TimeOfDay): Date => {
  const date = new Date(baseDate);
  date.setHours(timeOfDay.hour, timeOfDay.minute, 0, 0);
  return date;
};

const isTimeInRange = (
  currentTime: Date,
  startsAt: Date,
  endsAt: Date,
): boolean => {
  return currentTime >= startsAt && currentTime < endsAt;
};

const isDayApplicable = (days: DaysOfWeek, day: number): boolean => {
  switch (day) {
    case 0:
      return days.sunday;
    case 1:
      return days.monday;
    case 2:
      return days.tuesday;
    case 3:
      return days.wednesday;
    case 4:
      return days.thursday;
    case 5:
      return days.friday;
    case 6:
      return days.saturday;
    default:
      return false;
  }
};

export const isMenuAvailable = (
  menu: Menu | MenuShort,
  currentTime: Date,
): boolean => {
  if (!menu || !menu.availability.length) return false;

  for (const availability of menu.availability) {
    // If start or end time is nil, the menu is invalid
    if (!availability.startsAt || !availability.endsAt) {
      return false;
    }

    const startTime = availability.startsAt;
    const endTime = availability.endsAt;

    const startDate = createDate(currentTime, startTime);
    const endDate = createDate(currentTime, endTime);

    let availabilityFromPrevDayOfWeek = false;
    // Check if the end time is the same or before the start time
    // If it is then the end time refers to that time the next day
    if (
      endTime.hour < startTime.hour ||
      (endTime.hour === startTime.hour && endTime.minute <= startTime.minute)
    ) {
      // Okay in this case we allow current time to be < end time or >= start time
      // In the case that it is < end time, we are using availability from the previous day,
      // This means the day check needs to check if the previous day matches
      if (currentTime < endDate) {
        // We have met the time requirement. Record that the day of the week requirement is for prev day.
        availabilityFromPrevDayOfWeek = true;
      } else if (currentTime < startDate) {
        // This code path occurs if endDate <= currentTime < startDate which is possible since endTime <= startTime
        // This is also the only condition where the time requirement is not satisfied for endTime <= startTime
        continue;
      }
    } else if (!isTimeInRange(currentTime, startDate, endDate)) {
      // In this case start time < endTime, and the current time doesn't fit in that range
      // So simply return say not available
      continue;
    }

    // Check if the menu is applicable for the current day
    const dayOfWeek = availabilityFromPrevDayOfWeek
      ? getDay(subDays(currentTime, 1))
      : getDay(currentTime);

    if (isDayApplicable(availability.applies, dayOfWeek)) {
      return true;
    }
  }

  return false;
};

// The end Date of the availability window that is active at `currentTime`, or
// null if the menu isn't open then (or is open all-day, which has no meaningful
// closing time). Mirrors isMenuAvailable's window-matching, incl. overnight
// windows, but returns the window's end instead of a boolean.
const activeWindowEnd = (
  menu: Menu | MenuShort,
  currentTime: Date
): Date | null => {
  if (!menu?.availability?.length) return null;

  for (const availability of menu.availability) {
    if (!availability.startsAt || !availability.endsAt) continue;

    const startTime = availability.startsAt;
    const endTime = availability.endsAt;

    // All-day (start == end): open, but no bounded closing time.
    if (startTime.hour === endTime.hour && startTime.minute === endTime.minute) {
      continue;
    }

    const startDate = createDate(currentTime, startTime);
    let endDate = createDate(currentTime, endTime);
    let availabilityFromPrevDayOfWeek = false;

    const overnight =
      endTime.hour < startTime.hour ||
      (endTime.hour === startTime.hour && endTime.minute < startTime.minute);

    if (overnight) {
      if (currentTime < endDate) {
        availabilityFromPrevDayOfWeek = true;
      } else if (currentTime < startDate) {
        continue;
      } else {
        // Within the window that runs past midnight into tomorrow.
        endDate = addDays(endDate, 1);
      }
    } else if (!isTimeInRange(currentTime, startDate, endDate)) {
      continue;
    }

    const dayOfWeek = availabilityFromPrevDayOfWeek
      ? getDay(subDays(currentTime, 1))
      : getDay(currentTime);

    if (isDayApplicable(availability.applies, dayOfWeek)) {
      return endDate;
    }
  }

  return null;
};

// A short "Open until <time>" label for a menu card, in the site time zone, or
// null when there is no bounded closing time (no windows / all-day / closed —
// a closed menu is conveyed by its "Not available" badge instead). `now` is
// injectable for tests.
export const menuHoursLabel = (
  menu: MenuShort,
  timeZone?: string,
  now: Date = new Date()
): string | null => {
  if (!menu.availability?.length) return null;
  const zoned = timeZone ? toZonedTime(now, timeZone) : new Date(now);
  const end = activeWindowEnd(menu, zoned);
  return end ? `Open until ${format(end, "h:mm a")}` : null;
};

// Wrapper function that handles timezone conversion
export const isMenuOpen = (
  menu: Menu | MenuShort,
  requestedReadyTime?: string | null | undefined,
  timeZone?: string,
): boolean => {
  const readyTime = requestedReadyTime ?? new Date();
  if (!menu.availability.length) return false;
  const zonedReadyTime = timeZone
    ? toZonedTime(readyTime, timeZone)
    : new Date(readyTime);
  return isMenuAvailable(menu, zonedReadyTime);
};

// Helper function to get next available time with timezone support
export const getNextAvailableTime = (
  menu: Menu,
  timeZone: string = "UTC",
): string => {
  if (!menu.availability.length) return "not available";

  const currentTime = toZonedTime(new Date(), timeZone);

  for (const availability of menu.availability) {
    if (!availability.startsAt || !availability.endsAt) continue;
    if (!isDayApplicable(availability.applies, getDay(currentTime))) continue;

    const startDate = createDate(currentTime, availability.startsAt);
    if (startDate <= currentTime) continue;

    return `today at ${format(startDate, "h:mm a")}`;
  }

  // If not available today, check tomorrow
  const tomorrow = addDays(currentTime, 1);
  for (const availability of menu.availability) {
    if (!availability.startsAt || !availability.endsAt) continue;
    if (!isDayApplicable(availability.applies, getDay(tomorrow))) continue;

    const startDate = createDate(tomorrow, availability.startsAt);
    return `tomorrow at ${format(startDate, "h:mm a")}`;
  }

  return "not available";
};

export const getActiveMenu = (
  menus: MenuShort[],
  requestedReadyTime?: string | null | undefined,
  timeZone?: string,
) => {
  return menus.length
    ? (menus.find((menu) => isMenuOpen(menu, requestedReadyTime, timeZone)) ??
        menus[0])
    : null;
};

/**
 * Decide which menu code to show. Returns the first `preferred` candidate (URL
 * menuCode, then the working cart's menu) that is actually one of THIS order
 * type's `menus` AND not time-closed; otherwise the active menu. A candidate
 * that isn't in `menus` (e.g. an ONLINE menu code carried into a CATERING visit)
 * is skipped — fetching it would 404 → 500.
 *
 * Time-scheduled menus: a candidate whose menu has availability windows but
 * isn't open at `requestedReadyTime` is also skipped, so a cart carried on a
 * now-closed menu (e.g. a lunch item after the time moves to breakfast) falls
 * through to the active menu and is cleared downstream. Menus with no windows
 * are always eligible (unchanged). Returns null only when there are no menus.
 */
export const resolveMenuCode = (
  menus: MenuShort[],
  preferred: (string | null | undefined)[],
  requestedReadyTime?: string | null,
  timeZone?: string
): string | null => {
  const byCode = new Map(menus.map((m) => [m.code, m]));
  for (const candidate of preferred) {
    const menu = candidate ? byCode.get(candidate) : undefined;
    if (
      menu &&
      (!menu.availability?.length ||
        isMenuOpen(menu, requestedReadyTime, timeZone))
    ) {
      return menu.code;
    }
  }
  return getActiveMenu(menus, requestedReadyTime, timeZone)?.code ?? null;
};

export const getProductsList = (menu: Menu) =>
  uniqBy(
    menu?.tabs.flatMap((tab) => tab.items),
    (x) => x.productCode,
  );
